import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import SitesService from '../../services/SitesService';
import { isFieldConfigurable as checkIsConfigurable, isFieldFixed as checkIsFixed } from './utils/fieldUtils';

const SitesContext = createContext();

export const useSitesConfig = () => {
  const context = useContext(SitesContext);
  if (!context) {
    throw new Error('useSitesConfig must be used within SitesProvider');
  }
  return context;
};

export const SitesProvider = ({ children }) => {
  const [config, setConfig] = useState({
    metadata: {
      empresaId: 'default',
      templateId: 'basico',
      domain: null,
      tenantId: 'default-tenant',
      apiEndpoint: 'localhost:3000/api',
      apiKey: null
    },
    siteConfig: {
      company: {
        name: '',
        logo: null,
        tagline: '',
        description: ''
      },
      nav: {
        links: []
      },
      hero: {
        type: 'image',
        title: '',
        subtitle: '',
        badgeText: '',
        description: '',
        ctaText: '',
        ctaLink: '',
        secondaryCtaText: '',
        secondaryCtaLink: '',
        backgroundImage: '',
        carouselImages: []
      },
      locations: {
        title: '',
        subtitle: '',
        items: []
      },
      newsletter: {
        title: '',
        subtitle: '',
        placeholder: '',
        submitText: '',
        successMessage: ''
      },
      careers: {
        title: '',
        subtitle: '',
        description: '',
        endpoint: '/careers',
        positions: [],
        applyText: '',
        noPositionsText: ''
      },
      products: [],
      categories: [],
      shopFilters: {
        title: '',
        filters: [],
        priceRanges: []
      },
      contact: {
        title: '',
        subtitle: '',
        email: '',
        phone: '',
        address: '',
        endpoint: '/contact',
        formFields: {
          namePlaceholder: '',
          emailPlaceholder: '',
          messagePlaceholder: '',
          submitText: ''
        }
      },
      footer: {
        copyright: '',
        socialLinks: []
      },
      texts: {
        currency: '$',
        addToCart: '',
        viewProduct: '',
        shopTitle: '',
        shopSubtitle: '',
        allCategories: '',
        emptyCart: '',
        cartTitle: '',
        checkout: '',
        clearFilters: '',
        priceFilter: '',
        categoryFilter: '',
        resultsCount: '',
        searchPlaceholder: ''
      },
      menu: {
        title: '',
        subtitle: '',
        description: '',
        emptyStateText: '',
        categories: []
      },
      reviews: {
        title: '',
        subtitle: '',
        items: []
      },
      about: {
        title: '',
        subtitle: '',
        description: '',
        values: []
      },
      pages: {}
    },
    theme: {
      colors: {
        primary: '#F97415',
        primaryForeground: '#FFFFFF',
        secondary: '#EBE5E0',
        secondaryForeground: '#38322E',
        background: '#FBFAF9',
        foreground: '#1C1917',
        accent: '#F97415',
        accentForeground: '#FFFFFF',
        muted: '#EDEAE9',
        mutedForeground: '#7C716A',
        border: '#E5E0DC'
      },
      fonts: {
        sans: ['Inter', 'system-ui', 'sans-serif'],
        display: ['Playfair Display', 'Georgia', 'serif']
      },
      borderRadius: '0.75rem'
    }
  });

  const [editableFields, setEditableFields] = useState({
    templateId: null,
    configurableFields: [],
    fixedFields: [],
    fieldMetadata: {} // Metadatos de campos (ej: type_values)
  });
  const [loadingFields, setLoadingFields] = useState(false);

  // Cargar campos editables
  const loadEditableFields = useCallback(async () => {
    try {
      setLoadingFields(true);
      const response = await SitesService.getEditableFields();
      // El backend devuelve { error: false, status_code: 200, data: { templateId, configurableFields, fixedFields, fieldMetadata } }
      const fieldsData = response?.data || response;
      console.log('[SitesContext] Editable fields loaded:', fieldsData);
      
      // Extraer configurableFields y fixedFields (pueden ser arrays de strings o objetos con metadata)
      let configurableFieldsList = [];
      let fieldMetadataMap = {};
      
      if (Array.isArray(fieldsData.configurableFields)) {
        fieldsData.configurableFields.forEach(field => {
          if (typeof field === 'string') {
            configurableFieldsList.push(field);
          } else if (field.path) {
            // Es un objeto con metadata
            configurableFieldsList.push(field.path);
            if (field.type_values) {
              fieldMetadataMap[field.path] = { type_values: field.type_values };
            }
          }
        });
      }
      
      // Si hay fieldMetadata separado, combinarlo
      if (fieldsData.fieldMetadata) {
        fieldMetadataMap = { ...fieldMetadataMap, ...fieldsData.fieldMetadata };
      }
      
      setEditableFields({
        templateId: fieldsData.templateId,
        configurableFields: configurableFieldsList,
        fixedFields: fieldsData.fixedFields || [],
        fieldMetadata: fieldMetadataMap,
        generalSections: Array.isArray(fieldsData.generalSections) ? fieldsData.generalSections : [],
        pages: Array.isArray(fieldsData.pages) ? fieldsData.pages : []
      });
    } catch (err) {
      console.error('[SitesContext] Error loading editable fields:', err);
    } finally {
      setLoadingFields(false);
    }
  }, []);

  // Cargar configuración draft (en edición)
  const loadConfig = useCallback(async () => {
    try {
      const response = await SitesService.getDraftConfig();
      // El backend devuelve { error: false, status_code: 200, data: { metadata, siteConfig, theme } }
      // SitesService ya extrae response.data?.data || response.data
      const configData = response;
      console.log('[SitesContext] Draft config loaded:', configData);
      if (configData) {
        // El backend entrega el snapshot efectivo del tenant. No se mezclan
        // defaults locales del dashboard para evitar filtrar datos de otro sitio.
        setConfig({
          metadata: configData.metadata || {},
          siteConfig: configData.siteConfig || {},
          theme: configData.theme || {}
        });
      }
    } catch (err) {
      console.error('[SitesContext] Error loading draft config:', err);
    }
  }, []);

  useEffect(() => {
    // Cargar campos editables y configuración al montar
    loadEditableFields();
    loadConfig();
  }, [loadEditableFields, loadConfig]);

  const handleChange = (path, value) => {
    setConfig(prevConfig => {
      const newConfig = { ...prevConfig };
      const keys = path.split('.');
      let current = newConfig;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const handleArrayChange = (path, index, field, value) => {
    setConfig(prevConfig => {
      const newConfig = { ...prevConfig };
      const keys = path.split('.');
      let current = newConfig;
      
      for (const key of keys) {
        if (!current[key]) {
          current[key] = [];
        }
        current = current[key];
      }
      
      current[index] = { ...current[index], [field]: value };
      return newConfig;
    });
  };

  const handleAddArrayItem = (path, defaultItem) => {
    setConfig(prevConfig => {
      const newConfig = { ...prevConfig };
      const keys = path.split('.');
      let current = newConfig;
      
      for (const key of keys) {
        if (!current[key]) {
          current[key] = [];
        }
        current = current[key];
      }
      
      current.push(defaultItem);
      return newConfig;
    });
  };

  const handleRemoveArrayItem = (path, index) => {
    setConfig(prevConfig => {
      const newConfig = { ...prevConfig };
      const keys = path.split('.');
      let current = newConfig;
      
      for (const key of keys) {
        current = current[key];
      }
      
      current.splice(index, 1);
      return newConfig;
    });
  };

  // Función helper para obtener los valores posibles de un campo (type_values)
  const getFieldTypeValues = useCallback((fieldPath) => {
    return editableFields?.fieldMetadata?.[fieldPath]?.type_values || null;
  }, [editableFields]);

  return (
    <SitesContext.Provider value={{
      config,
      setConfig,
      editableFields,
      loadingFields,
      getFieldTypeValues,
      loadConfig,
      handleChange,
      handleArrayChange,
      handleAddArrayItem,
      handleRemoveArrayItem,
      isFieldConfigurable: (fieldPath) => {
        if (!editableFields.configurableFields) return false;
        return checkIsConfigurable(fieldPath, editableFields.configurableFields);
      },
      isFieldFixed: (fieldPath) => {
        if (!editableFields.fixedFields) return false;
        return checkIsFixed(fieldPath, editableFields.fixedFields);
      }
    }}>
      {children}
    </SitesContext.Provider>
  );
};

export default SitesContext;
